import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BookOpen, Menu, X } from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Gen Z', path: '/gen-z' },
    { name: 'Our Books', path: '/books' },
    { name: 'Videos', path: '/videos' },
    { name: 'Daily Word', path: '/daily-word' },
    { name: 'Faith Teachings', path: '/faith-teachings' },
    { name: 'Story Library', path: '/library/story' },
    { name: 'Audio Library', path: '/library/audio' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
    { name: 'Donate', path: '/donate' },
  ];

  return (
    <nav className="glass-nav sticky top-0 z-50 border-b border-slate-200/50" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <a
            href="https://restoremyword.net"
            className="flex-shrink-0 font-heading text-xl md:text-2xl font-semibold tracking-tight text-brand-primary hover:text-brand-accent transition-colors duration-300"
            data-testid="nav-logo"
          >
            restoremyword<span className="text-brand-accent">.net</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center gap-6 ml-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="whitespace-nowrap font-body text-sm font-medium text-slate-700 hover:text-brand-accent transition-colors duration-300"
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
            {user && user.role === 'admin' && (
              <Link
                to="/admin"
                className="font-body text-sm font-medium text-brand-primary hover:text-brand-accent transition-colors duration-300"
                data-testid="nav-link-admin"
              >
                Admin
              </Link>
            )}
            {user && user !== false ? (
              <button
                onClick={handleLogout}
                className="font-body text-sm font-medium px-4 py-2 bg-transparent border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white transition-all duration-300 rounded"
                data-testid="nav-logout-button"
              >
                Logout
              </button>
            ) : (
              <Link
                to="/login"
                className="font-body text-sm font-medium px-4 py-2 bg-brand-accent text-white hover:bg-brand-accent-hover transition-all duration-300 rounded"
                data-testid="nav-login-button"
              >
                Login
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="xl:hidden text-brand-primary"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="xl:hidden py-4 border-t border-slate-200" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 font-body text-sm font-medium text-slate-700 hover:text-brand-accent transition-colors duration-300"
              >
                {link.name}
              </Link>
            ))}
            {user && user.role === 'admin' && (
              <Link
                to="/admin"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 font-body text-sm font-medium text-brand-primary hover:text-brand-accent transition-colors duration-300"
              >
                Admin
              </Link>
            )}
            {user && user !== false ? (
              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="w-full mt-2 font-body text-sm font-medium px-4 py-2 bg-transparent border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white transition-all duration-300 rounded"
              >
                Logout
              </button>
            ) : (
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block mt-2 text-center font-body text-sm font-medium px-4 py-2 bg-brand-accent text-white hover:bg-brand-accent-hover transition-all duration-300 rounded"
              >
                Login
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;